import React, { useMemo, useState } from 'react';
import { IconButton, Stack, Box, Tooltip } from '@mui/material';
import { formatDateOnly } from '../../Formatters';
import { Link } from 'react-router-dom';
import { Delete, PersonOff } from '@mui/icons-material';
import { RemoveUserAccessDialog } from './RemoveUserAccessDialog';
import { useQuery } from '@apollo/client/react';
import { GET_USER_DATA_FOR_SUBSCRIPTION } from './UsersInLicense.graphql';
import { UserAccess, User } from '../../interfaces';
import { Counter } from '../../components/Counter';
import { ExcelExportLicenseUsers } from './ExcelExportLicenseUsers';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';

export interface Props {
  users: UserAccess[];
  licenseId: number;
  subscriptionId: number;
}

export function UserTable({ users, licenseId, subscriptionId }: Props) {
  const [currentUser, setCurrentUser] = useState<UserAccess | null>(null);

  const userIds = users.map(userAccess => userAccess.user.id);
  const { loading, error, data } = useQuery<{ users: { id: string; userId: number; guideViewsCount: number }[] }>(GET_USER_DATA_FOR_SUBSCRIPTION, {
    fetchPolicy: 'no-cache',
    variables: { licenseId, subscriptionId, userIds },
  });

  const getGuideViews = (user: User) => {
    if (error) return 'Feilet';
    if (loading || !data) return 'Henter..';

    const userData = data.users.find((x) => x.userId === user.id);
    if (!userData) return 0;

    return userData.guideViewsCount.toLocaleString('nb-NO');
  };

  const columns = useMemo<GridColDef<UserAccess>[]>(
    () => [
      {
        field: 'displayName',
        headerName: 'Navn',
        minWidth: 120,
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        renderCell: (params) => <Link to={'/user/' + params.row.user.id}>{params.row.user.displayName}</Link>,
      },
      {
        field: 'email',
        headerName: 'E-post',
        minWidth: 120,
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (_, row) => row.user.email,
      },
      {
        field: 'lastLoggedIn',
        headerName: 'Sist innlogget',
        minWidth: 120,
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (_, row) => formatDateOnly(row.user.lastLoggedIn),
      },
      {
        field: 'guideViews',
        headerName: 'Sidevisninger',
        description: 'Antall oppslag de siste 12 måneder',
        minWidth: 120,
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        valueGetter: (_, row) => getGuideViews(row.user),
      },
      {
        field: 'actions',
        headerName: '',
        width: 48,
        minWidth: 48,
        maxWidth: 48,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        headerAlign: 'left',
        align: 'left',
        renderCell: (params) => {
          const isDeleted = params.row.isDeleted === true;

          if (isDeleted) {
            return (
              <Tooltip title="Fjernet">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', color: 'error.main' }}>
                  <PersonOff fontSize="small" />
                </Box>
              </Tooltip>
            );
          }

          return (
            <IconButton title="Fjern brukertilgang" onClick={() => setCurrentUser(params.row)} size="large">
              <Delete />
            </IconButton>
          );
        },
      },
    ],
    [getGuideViews],
  );

  return (
    <Box sx={{ width: '100%' }}>
      {currentUser && (
        <RemoveUserAccessDialog
          userAccessId={currentUser.id}
          userId={currentUser.user.id}
          displayName={currentUser.user.displayName}
          licenseId={licenseId}
          onClose={() => setCurrentUser(null)}
        />
      )}

      <Stack direction="row" sx={{alignItems: 'center', justifyContent: 'flex-end'}}>
        <ExcelExportLicenseUsers
          users={users ?? []}
          licenseId={licenseId}
          subscriptionId={subscriptionId}
          analyticsData={data?.users || null}
          isLoadingAnalytics={loading}
        />
        <Counter list={users} />
      </Stack>
      <Box sx={{ width: '100%' }}>
        <DataGridPro
          autoHeight
          density="compact"
          rows={users}
          columns={columns}
          getRowId={(row) => row.id}
          localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
          pagination
          pageSizeOptions={[50, 100, 250]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 50,
                page: 0,
              },
            },
          }}
          sx={{
            mt: 1,
            width: '100%',
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
            },
          }}
        />
      </Box>
    </Box>
  );
}